import type { ReactNode } from 'react'
import { AlertTriangle, Ban, CheckCircle2, Hourglass, Inbox, PencilLine, Send, XCircle, type LucideIcon } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { tagText } from '../lib/texte'
import type { ApprovalRun, IncomingInvoiceDetail } from '../types/incoming-invoices.types'

const FARBE = {
  neutral: 'border-border bg-muted/40 text-foreground',
  blau: 'border-sky-500/50 bg-sky-500/10 text-sky-900 dark:text-sky-100',
  gelb: 'border-amber-500/60 bg-amber-500/15 text-amber-950 dark:text-amber-100',
  rot: 'border-destructive/60 bg-destructive/10 text-destructive',
  gruen: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
} as const

const namen = (liste: string[]) => (liste.length <= 1 ? (liste[0] ?? '') : `${liste.slice(0, -1).join(', ')} und ${liste[liste.length - 1]}`)

/**
 * **Wo steht die Rechnung, und worauf wartet sie?** — ein Balken über der Maske, der das auf den ersten
 * Blick sagt (Auftraggeber 5.10.2026). Er wiederholt nichts, was man bedienen muss: Entschieden wird im
 * Abschnitt „Freigabe", behoben in der Erfassung.
 */
export function Statusleiste({ rechnung, lauf }: { rechnung: IncomingInvoiceDetail; lauf: ApprovalRun | null }) {
  const offen = rechnung.checks.blocking.length
  const anlaesse = rechnung.checks.reasons.length
  let farbe: keyof typeof FARBE = 'neutral'
  let Symbol: LucideIcon = PencilLine
  let titel: ReactNode = ''
  let text: ReactNode = null

  switch (rechnung.status) {
    case 'UNCAPTURED':
      farbe = 'blau'
      Symbol = Inbox
      titel = 'Im Eingang — noch nicht erfasst'
      text = 'Wähle den Lieferanten, dann bekommt die Rechnung ihre Eingangsnummer.'
      break
    case 'DRAFT':
      if (offen > 0) {
        farbe = 'gelb'
        Symbol = PencilLine
        titel = `In Erfassung — noch ${offen} ${offen === 1 ? 'Punkt' : 'Punkte'} vor dem Abschließen`
        text = 'Was fehlt, steht unter „Prüfung".'
      } else {
        farbe = 'gruen'
        Symbol = Send
        titel = 'In Erfassung — bereit zum Abschließen'
        text = anlaesse > 0 ? `${anlaesse} ${anlaesse === 1 ? 'Anlass' : 'Anlässe'} für eine Freigabe — siehe „Prüfung".` : 'Nichts zu beanstanden.'
      }
      break
    case 'IN_APPROVAL': {
      const wartend = (lauf?.tasks ?? []).filter((a) => a.status === 'OPEN')
      const meine = wartend.some((a) => a.mine)
      const andere = [...new Set(wartend.filter((a) => !a.mine).map((a) => a.approverName))]
      farbe = 'gelb'
      Symbol = Hourglass
      titel = meine ? 'Wartet auf deine Freigabe' : 'Wartet auf Freigabe'
      text =
        wartend.length === 0 ? null : meine ? (
          andere.length > 0 ? (
            <>
              Außerdem offen bei <strong>{namen(andere)}</strong>.
            </>
          ) : (
            'Entscheiden kannst du im Abschnitt „Freigabe".'
          )
        ) : (
          <>
            Offen bei <strong>{namen(andere)}</strong>.
          </>
        )
      break
    }
    case 'REJECTED': {
      const abgelehnt = (lauf?.tasks ?? []).find((a) => a.status === 'REJECTED')
      farbe = 'rot'
      Symbol = XCircle
      titel = 'Freigabe abgelehnt'
      text = abgelehnt ? (
        <>
          von <strong>{abgelehnt.approverName}</strong>
          {abgelehnt.comment ? ` — „${abgelehnt.comment}"` : ''}
        </>
      ) : null
      break
    }
    case 'RELEASED':
    case 'EXPORTED': {
      const uebergeben = rechnung.status === 'EXPORTED'
      farbe = 'gruen'
      Symbol = CheckCircle2
      titel = uebergeben ? 'Freigegeben und an die Buchhaltung übergeben' : 'Freigegeben'
      if (rechnung.paymentBlocked) {
        farbe = 'gelb'
        Symbol = AlertTriangle
        text = `Zahlung gesperrt${rechnung.paymentBlockedReason ? ` — ${rechnung.paymentBlockedReason}` : ''}`
      } else if (rechnung.paymentStatus === 'PAID') text = 'Bezahlt.'
      else if (rechnung.paymentStatus === 'OVERDUE') {
        farbe = 'rot'
        Symbol = AlertTriangle
        text = `Überfällig seit ${tagText(rechnung.dueDate)} — offen ${geldText(rechnung.openAmount)}`
      } else if (rechnung.paymentStatus === 'PARTIALLY_PAID') text = `Teilweise bezahlt — offen ${geldText(rechnung.openAmount)}${rechnung.dueDate ? `, fällig am ${tagText(rechnung.dueDate)}` : ''}`
      else if (rechnung.paymentStatus === 'OPEN') text = `Wartet auf Zahlung — offen ${geldText(rechnung.openAmount)}${rechnung.dueDate ? `, fällig am ${tagText(rechnung.dueDate)}` : ''}`
      break
    }
    case 'CANCELLED':
      Symbol = Ban
      titel = 'Storniert'
      text = rechnung.cancelReason
      break
  }

  return (
    <div role="status" className={`flex items-start gap-3 rounded-lg border-2 px-4 py-3 ${FARBE[farbe]}`}>
      <Symbol size={22} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-base font-semibold leading-tight">{titel}</p>
        {text && <p className="mt-0.5 text-sm">{text}</p>}
      </div>
    </div>
  )
}
